import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { handleError } from '../../../../../utils/ErrorHandler';

const initialState = {
  list: [],
  promoCode: null,
  error: "",
  isFetchingAll: false,    // For getPromoCodes
  isFetchingSingle: false, // For getPromoCodeById
  isSubmitting: false,     // For createPromoCode and updatePromoCode
  isDeleting: false,       // For deletePromoCode
  companyList: [],
  companyLoading: false,
};

// Get all promo codes
export const getPromoCodes = createAsyncThunk(
  'promoCodes/getPromoCodes',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/promo-code');
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to fetch promo codes");
      return rejectWithValue(errorMessage);
    }
  }
);

// Create promo code
export const createPromoCode = createAsyncThunk(
  "promoCodes/createPromoCode",
  async (values, { rejectWithValue }) => {
    try {
      const formattedData = {
        ...values,
        start_date: new Date(values.start_date).toISOString(),
        end_date: new Date(values.end_date).toISOString(),
      };

      const { data } = await axios.post('/promo-code', formattedData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      toast.success("Promo code created successfully!");
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to create promo code");
      return rejectWithValue(errorMessage);
    }
  }
);



// Delete promo code
export const deletePromoCode = createAsyncThunk(
  "promoCodes/deletePromoCode",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(`/promo-code/${id}`);
      dispatch(getPromoCodes()); // Refresh the list after deletion
      toast.success("Promo code deleted successfully!");
      return id;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to delete promo code");
      return rejectWithValue(errorMessage);
    }
  }
);

// Update promo code
export const updatePromoCode = createAsyncThunk(
  "promoCodes/updatePromoCode",
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const formattedData = {
        ...values,
        start_date: new Date(values.start_date).toISOString(),
        end_date: new Date(values.end_date).toISOString(),
      };

      const { data } = await axios.put(`/promo-code/${id}`, formattedData);
      toast.success("Promo code updated successfully!");
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to update promo code");
      return rejectWithValue(errorMessage);
    }
  }
);

const promoCodeSlice = createSlice({
  name: 'promoCodes',
  initialState,
  reducers: {
    clearPromoCode: (state) => {
      state.promoCode = null;
    },
  },
  extraReducers: (builder) => {
    // getPromoCodes
    builder.addCase(getPromoCodes.pending, (state) => {
      state.isFetchingAll = true;
      state.error = "";
    });
    builder.addCase(getPromoCodes.fulfilled, (state, action) => {
      state.isFetchingAll = false;
      state.list = action.payload;
    });
    builder.addCase(getPromoCodes.rejected, (state, action) => {
      state.isFetchingAll = false;
      state.error = action.payload;
    });

    // createPromoCode
    builder.addCase(createPromoCode.pending, (state) => {
      state.isSubmitting = true;
      state.error = "";
    });
    builder.addCase(createPromoCode.fulfilled, (state, action) => {
      state.isSubmitting = false;
      state.list.push(action.payload);
    });
    builder.addCase(createPromoCode.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = action.payload;
    });

    // deletePromoCode
    builder.addCase(deletePromoCode.pending, (state) => {
      state.isDeleting = true;
      state.error = "";
    });
    builder.addCase(deletePromoCode.fulfilled, (state, action) => {
      state.isDeleting = false;
      state.list = state.list.filter((promo) => promo.id !== action.payload);
    });
    builder.addCase(deletePromoCode.rejected, (state, action) => {
      state.isDeleting = false;
      state.error = action.payload;
    });

    // updatePromoCode
    builder.addCase(updatePromoCode.pending, (state) => {
      state.isSubmitting = true;
      state.error = "";
    });
    builder.addCase(updatePromoCode.fulfilled, (state, action) => {
      state.isSubmitting = false;
      const updatedPromo = action.payload;
      state.list = state.list.map((promo) =>
        promo.id === updatedPromo.id ? updatedPromo : promo
      );
      state.promoCode = updatedPromo;
    });
    builder.addCase(updatePromoCode.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = action.payload;
    });
  }
});

export default promoCodeSlice.reducer;
export const { clearPromoCode } = promoCodeSlice.actions;
